import React, { useState, useEffect } from 'react';
import {
    Dialog,
    Typography,
    Button,
    IconButton,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Tooltip,
    TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MoveIcon from '@mui/icons-material/DriveFileMove';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import styled from '@mui/system/styled';
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useOcrJobs } from '../../Contexts/OcrJobsContext';
import {
    getAllowedTransferTypes,
    downloadDocumentFiles,
    buildTransferDto,
    enqueueIncomesExpensesOcr,
    deleteSourceDocument,
    TRANSFER_DOC_TYPES,
    translateClarificationError,
} from './DocumentTransferUtils';
import ClarificationDocumentService from '../../Services/clarificationDocuments.service';

const MySwal = withReactContent(Swal);

// The dialog is rendered from inside the edit modal (z-index 1300) and from modals that nest it,
// so both the MUI dialogs and the SweetAlert prompts need to be lifted above them.
const DIALOG_Z_INDEX = 2000;
const SWAL_HIGH_ZINDEX = { container: 'swal-high-zindex' };

const deleteSourcePrompt = (t) => ({
    title: t('DeleteSourceDocument'),
    text: t('DoYouWantToDeleteTheSourceDocument'),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('Yes'),
    cancelButtonText: t('No'),
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    heightAuto: false,
    customClass: SWAL_HIGH_ZINDEX,
});

const StyledDialog = styled(Dialog)({
    '& .MuiPaper-root': {
        width: '20%',
        maxWidth: '25%',
    },
});


const StyledDialogTitle = styled(DialogTitle)({
    fontSize: '1.8rem',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#025366',
});

const StyledDialogContent = styled(DialogContent)({
    textAlign: 'center',
    padding: '20px 30px',
});

const StyledDialogActions = styled(DialogActions)({
    justifyContent: 'center',
    paddingBottom: '20px',
});

const StyledButton = styled(Button)({
    margin: '0 8px',
    padding: '10px 20px',
    fontSize: '1.1rem',
    textTransform: 'none',
    color: '#fff',
    backgroundColor: '#E57C22',
    '&:hover': {
        backgroundColor: '#d9731a',
    },
});

const ConfirmButton = styled(StyledButton)({
    backgroundColor: '#4caf50',
    color: '#fff',
    '&:hover': {
        backgroundColor: '#388e3c',
    },
});

const OptionButton = styled(Button)({
    margin: '10px 0',
    padding: '12px 25px',
    fontSize: '1.1rem',
    textTransform: 'none',
    color: '#025366',
    backgroundColor: '#f5f5f5',
    border: '1px solid #E57C22',
    width: '100%',
    maxWidth: '200px',
    '&:hover': {
        backgroundColor: '#E57C22',
        color: '#fff',
    },
});

const DocumentTransferDialog = ({
    editedItem,
    token,
    setLoading,
    RefreshPage,
    t,
    client,
    hideIcon = false,
    externalOpen = false,
    onClose = null,
    onTransferComplete = null,
    /** When true, transfer icon restores the clarification row to its original type (no type picker). */
    clarificationRestoreMode = false,
}) => {
    const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedType, setSelectedType] = useState('');
    const [clarificationNote, setClarificationNote] = useState('');
    const navigate = useNavigate();
    const { addJobs } = useOcrJobs();

    useEffect(() => {
        if (externalOpen) {
            if (clarificationRestoreMode) {
                setSelectedType(TRANSFER_DOC_TYPES.DocumentsRequiringClarification);
                setIsConfirmDialogOpen(true);
            } else {
                setIsTypeDialogOpen(true);
            }
        }
    }, [externalOpen, clarificationRestoreMode]);
    
    const handleMoveClick = () => {
        if (clarificationRestoreMode) {
            setSelectedType(TRANSFER_DOC_TYPES.DocumentsRequiringClarification);
            setIsConfirmDialogOpen(true);
            return;
        }
        // An unknown document type has no target types, so show why instead of an empty picker.
        if (getAllowedTransferTypes(editedItem?.docType).length === 0) {
            toast.info(t('NoAvailableTransferTypes'));
            return;
        }
        setIsTypeDialogOpen(true);
    };
    
    const handleDialogClose = () => {
        setIsTypeDialogOpen(false);
        if (onClose) {
            onClose();
        }
    };

    const handleTypeSelection = (type) => {
        setSelectedType(type);
        setIsTypeDialogOpen(false);
        setIsConfirmDialogOpen(true);
    };

    const moveToClarification = async () => {
        const response = await ClarificationDocumentService.MoveToClarification({
            caseId: client.caseId,
            accountantId: client.accountantId,
            fromType: editedItem.docType,
            id: editedItem.id,
            clarificationNote: clarificationNote?.trim() || null,
        }, token);

        if (!response.ok) {
            const errorText = await response.text();
            toast.error(translateClarificationError(t, errorText));
            return false;
        }

        toast.success(t('SentToClarification') || 'Document sent to clarification');
        await RefreshPage?.();
        if (onTransferComplete) onTransferComplete();
        return true;
    };

    const restoreFromClarification = async () => {
        const response = await ClarificationDocumentService.RestoreFromClarification({
            caseId: client.caseId,
            accountantId: client.accountantId,
            clarificationDocumentId: editedItem.id,
        }, token);

        if (!response.ok) {
            const errorText = await response.text();
            toast.error(translateClarificationError(t, errorText, 'RestoreFromClarificationFailed'));
            return false;
        }

        toast.success(t('RestoredFromClarification') || 'Document restored');
        await RefreshPage?.();
        if (onTransferComplete) onTransferComplete();
        return true;
    };

    const handleConfirm = async () => {
        try {
            setLoading(true);
            setIsLoading(true);
            setIsConfirmDialogOpen(false);

            if (clarificationRestoreMode) {
                await restoreFromClarification();
                return;
            }

            if (selectedType === TRANSFER_DOC_TYPES.DocumentsRequiringClarification) {
                await moveToClarification();
                return;
            }

            // Keep legacy single-transfer file naming for backward compatibility.
            const files = await downloadDocumentFiles(editedItem, { useUniqueNames: false });

            const dto = buildTransferDto(editedItem, client, selectedType);

            if (selectedType === 'Incomes' || selectedType === 'Expenses') {
                const jobIds = await enqueueIncomesExpensesOcr(dto, files, token, t, toast);
                
                if (jobIds && jobIds.length > 0) {
                    const result = await MySwal.fire(deleteSourcePrompt(t));

                    if (result.isConfirmed) {
                        await deleteSourceDocument(dto.fromType, dto.id, token, t, toast);
                    }

                    const reportingDate = dto.reportingMonthDate ? new Date(dto.reportingMonthDate) : new Date();
                    addJobs(jobIds, 'ocr', async () => {
                        await RefreshPage();
                    }, {
                        caseId: dto.caseId,
                        clientName: client?.name || '',
                        reportingMonth: `${reportingDate.getMonth() + 1}/${reportingDate.getFullYear()}`,
                        fileCount: files.length,
                    });

                    await RefreshPage();

                    if (onTransferComplete) {
                        onTransferComplete();
                    }
                }
            } else if (selectedType === 'PaymentAcceptances') {
                const result = await MySwal.fire(deleteSourcePrompt(t));

                sessionStorage.removeItem('hasTransferred');

                navigate('/PaymentAcceptance', { 
                    state: { 
                        type: "PaymentAcceptances", 
                        client, 
                        fileTransported: files,
                        dto, 
                        idToDelete: result.isConfirmed ? editedItem.id : null,
                        fileTypeToDelete: result.isConfirmed ? editedItem.docType : null,
                        requiresTransferDateSelection: true,
                        paymentAcceptanceType: dto.paymentAcceptanceType,
                    } 
                });
            }
            else if (selectedType === 'OtherDocuments') {
                const result = await MySwal.fire(deleteSourcePrompt(t));

                sessionStorage.removeItem('hasTransferredOtherDocuments');

                navigate('/OtherDocument', { 
                    state: { 
                        type: "OtherDocuments", 
                        client, 
                        fileTransported: files, 
                        dto,
                        fromSignatureDocuments: dto.fromType === 'SignatureDocuments',
                        fromForms: dto.fromType === 'Forms',
                        idToDelete: result.isConfirmed ? editedItem.id : null,
                        fileTypeToDelete: result.isConfirmed ? editedItem.docType : null
                    } 
                });
            }
        } catch (error) {
            console.error('Error during document transfer:', error);
            toast.error(error?.message || t('documentUploadError', { errorText: '' }));
        } finally {
            setLoading(false);
            setIsLoading(false);
        }
    };

    const allowedTypes = getAllowedTransferTypes(editedItem?.docType);
    const confirmTitle = clarificationRestoreMode
        ? (t('ConfirmRestoreFromClarification') || t('ConfirmTransfer'))
        : selectedType === TRANSFER_DOC_TYPES.DocumentsRequiringClarification
            ? (t('ConfirmSendToClarification') || t('ConfirmTransfer'))
            : t('ConfirmTransfer');
    const confirmBody = clarificationRestoreMode
        ? `${t('AreYouSureYouWantToRestoreFromClarification') || 'Restore this document to'} ${t(editedItem?.originalDocumentType || 'document')}?`
        : selectedType === TRANSFER_DOC_TYPES.DocumentsRequiringClarification
            ? (t('AreYouSureYouWantToSendToClarification') || t('AreYouSureYouWantToTransferDocument'))
            : `${t('AreYouSureYouWantToTransferDocument')} ${t(selectedType)}?`;

    return (
        <>
            {!hideIcon && (
                <Tooltip title={clarificationRestoreMode ? (t('RestoreFromClarification') || t('TransferDocument')) : t('TransferDocument')}>
                    <IconButton onClick={handleMoveClick} disabled={isLoading}>
                        <MoveIcon style={{ color: '#E57C22' }} />
                    </IconButton>
                </Tooltip>
            )}

            <StyledDialog open={isTypeDialogOpen} onClose={handleDialogClose} sx={{ zIndex: DIALOG_Z_INDEX }}>
                <StyledDialogTitle>{t("SelectDocumentType")}</StyledDialogTitle>
                <StyledDialogContent>
                    <Typography variant="body2" color="#025366">{t("ChooseTypeToTransferDocument")}:</Typography>
                    <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                        {allowedTypes.map((type) => (
                            <OptionButton key={type} onClick={() => handleTypeSelection(type)}>
                                {t(type)}
                            </OptionButton>
                        ))}
                    </Box>

                </StyledDialogContent>
            </StyledDialog>

            <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)} sx={{ zIndex: DIALOG_Z_INDEX }}>
                <StyledDialogTitle>{confirmTitle}</StyledDialogTitle>
                <StyledDialogContent>
                    <Typography variant="body1" color="#025366">
                        {confirmBody}
                    </Typography>
                    {!clarificationRestoreMode && selectedType === TRANSFER_DOC_TYPES.DocumentsRequiringClarification && (
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            margin="normal"
                            label={t('ClarificationNote')}
                            placeholder={t('ClarificationNotePlaceholder')}
                            value={clarificationNote}
                            onChange={(e) => setClarificationNote(e.target.value)}
                        />
                    )}
                </StyledDialogContent>
                <StyledDialogActions>
                    <ConfirmButton onClick={handleConfirm} startIcon={<TransferWithinAStationIcon style={{ marginRight: '10px' }} />}>
                        {t("Confirm")}
                    </ConfirmButton>
                </StyledDialogActions>
            </Dialog>

           
        </>
    );
};

export default DocumentTransferDialog;
